import type { SkillTier } from '@highlight/common'
import { isAnswerableStagedAction } from '@renderer/chat/integrations/answerable'
import { isReportToUserStagedAction } from '@renderer/chat/integrations/report-to-user'
import { usePaywallState } from '@renderer/chat/store/paywalls'
import { useCreditsLock, type CreditsLock } from '../../components/sidepanel/plan-banners/credits-lock'
import type { StagedAction } from '../../hooks/staged-actions/types'
import { lockedTierOf, useIsActionLocked } from './locked-action'

/** Why an action is held back: spent credits, or a skill tier the plan doesn't reach. */
export type ActionLock = { kind: 'credits'; lock: CreditsLock } | { kind: 'tier'; tier: SkillTier }

type LockSubject = Pick<StagedAction, 'metadata' | 'archivedAt' | 'lastInvokedAt' | 'toolSet' | 'toolName'>

/**
 * Whether acting on the action spends credits. Sent and archived actions stay
 * readable, and so do a delivered report and a question waiting on an answer:
 * reading one or answering it costs nothing.
 */
const spendsCredits = (action: LockSubject) =>
  action.archivedAt === null &&
  action.lastInvokedAt === null &&
  !isReportToUserStagedAction(action) &&
  !isAnswerableStagedAction(action)

/**
 * The lock over an action, or null. Spent credits come first: unblurring a
 * tier-locked preview would spend a follow-up on a draft the credits can't pay for.
 */
export function useActionLock(action: LockSubject | undefined): ActionLock | null {
  const credits = useCreditsLock()
  const tierLocked = useIsActionLocked(action)
  if (!action) return null
  if (credits && spendsCredits(action)) return { kind: 'credits', lock: credits }
  const tier = tierLocked ? lockedTierOf(action) : null
  return tier ? { kind: 'tier', tier } : null
}

/**
 * Whether the action is held back, so drafting waits and its send and edit
 * controls stand down. Until the plan loads it is, like a tier lock: whether
 * credits are left isn't known yet, and drafting then would spend credits a
 * locked page is about to say are gone.
 */
export function useIsActionHeld(action: LockSubject | undefined): boolean {
  const lock = useActionLock(action)
  const { isLoading } = usePaywallState()
  return lock !== null || (isLoading && action !== undefined && spendsCredits(action))
}
